// src/AIDataExporter.js

export const generateAIReadyData = (comprehensiveReport, formData) => {
  const aiData = {
    metadata: {
      exportDate: new Date().toISOString(),
      reportType: "EHS & Insurance Risk Assessment",
      version: "1.0",
      instructions: "Upload this file to ChatGPT, Claude, or any AI assistant to get personalized EHS and insurance compliance guidance based on your assessment."
    },

    systemPrompt: {
      role: "You are an expert EHS (Environmental Health & Safety) and insurance compliance advisor. You have been provided with a comprehensive risk assessment for a company. Use this data to provide specific, actionable guidance tailored to their exact situation.",
      context: `
COMPANY PROFILE:
- Industry: ${formData.industry}
- Employees: ${formData.employeeCount}
- Revenue: ${formData.revenue}
- Number of Facilities: ${formData.facilities.length}
- Operating States: ${formData.states.join(', ')}
- Operations: ${formData.operations.join(', ')}

IDENTIFIED HAZARDS:
${formData.hazards.length > 0 ? formData.hazards.map(h => `- ${h}`).join('\n') : '- None specified'}

${formData.psmChemicals.length > 0 ? `
PSM-COVERED CHEMICALS (29 CFR 1910.119):
${formData.psmChemicals.map(c => `- ${c}`).join('\n')}
` : ''}

${formData.products.length > 0 ? `
PRODUCTS WITH EMERGING CONTAMINANTS:
${formData.products.map(p => `- ${p}`).join('\n')}
` : ''}

FACILITY DETAILS:
${formData.facilities.map((f, idx) => `
Facility ${idx + 1}:
- Age: ${f.age || 'Not specified'}
- Location: ${f.location || 'Not specified'}
- Air Emissions: ${f.airEmissions ? 'Yes' : 'No'}
- Water Discharges: ${f.waterDischarges ? 'Yes' : 'No'}
- Hazardous Waste Generation: ${f.hazardousWaste ? 'Yes' : 'No'}
- Hazardous Materials Storage: ${f.hazardousMaterialsStorage ? 'Yes' : 'No'}
- Past Remediation: ${f.pastRemediation ? 'Yes' : 'No'}
- Reportable Spills: ${f.reportableSpills ? 'Yes' : 'No'}
- Brownfields Program: ${f.brownfields ? 'Yes' : 'No'}
`).join('\n')}

INSURANCE POSTURE:
- Current Market: ${formData.insuranceType}
- Pollution Coverage: ${formData.hasPollutionCoverage ? 'Yes' : 'No'}

PRIORITY RISKS IDENTIFIED:
${comprehensiveReport.executiveSummary.topRisks.map((risk, idx) => `
${idx + 1}. ${risk.risk} (Severity: ${risk.severity})
   Timeline: ${risk.timeline}
   Estimated Cost: ${risk.estimatedCost}
`).join('\n')}

REGULATORY REQUIREMENTS:
${comprehensiveReport.regulatoryCompliance.oshaRequirements.map(req => `
- ${req.standard}
  Key Actions: ${req.actionItems.slice(0, 3).join('; ')}
`).join('\n')}

When answering questions:
1. Always reference specific regulations (CFR citations) when applicable
2. Provide cost estimates based on the assessment data
3. Prioritize immediate safety and compliance risks
4. Consider the company's size and resources when making recommendations
5. Flag when professional consultants (attorneys, EHS specialists, insurance brokers) should be engaged
6. Be specific about timelines and implementation steps
`,
      capabilities: [
        "Explain specific regulations and how they apply to this company",
        "Develop detailed compliance implementation plans",
        "Draft policies, procedures, and training programs",
        "Analyze gaps between current state and compliance requirements",
        "Recommend specific insurance coverages and policy language",
        "Calculate compliance costs and ROI",
        "Generate audit checklists and inspection preparation materials",
        "Draft communications to regulators or insurance carriers",
        "Create employee training materials",
        "Develop emergency response procedures"
      ]
    },

    companyProfile: {
      industry: formData.industry,
      employeeCount: formData.employeeCount,
      revenue: formData.revenue,
      numberOfFacilities: formData.facilities.length,
      operations: formData.operations,
      states: formData.states
    },

    facilities: formData.facilities.map((facility, idx) => ({
      facilityNumber: idx + 1,
      type: facility.type,
      age: facility.age,
      squareFootage: facility.squareFootage,
      location: facility.location,
      environmentalAspects: {
        airEmissions: facility.airEmissions,
        waterDischarges: facility.waterDischarges,
        hazardousWaste: facility.hazardousWaste,
        hazardousMaterialsStorage: facility.hazardousMaterialsStorage,
        dischargeType: facility.dischargeType,
        permits: facility.permits
      },
      historicalIssues: {
        pastRemediation: facility.pastRemediation,
        reportableSpills: facility.reportableSpills,
        brownfields: facility.brownfields
      },
      insuranceRequirements: facility.insuranceRequirements
    })),

    hazards: {
      general: formData.hazards,
      psmChemicals: formData.psmChemicals,
      products: formData.products
    },

    riskAssessment: {
      priorityRisks: comprehensiveReport.executiveSummary.topRisks,
      priorityActions: comprehensiveReport.executiveSummary.priorityActions
    },

    regulatoryRequirements: {
      osha: comprehensiveReport.regulatoryCompliance.oshaRequirements.map(req => ({
        standard: req.standard,
        requirement: req.requirement,
        actionItems: req.actionItems,
        training: req.training,
        estimatedCosts: req.costs,
        chemicalsPresent: req.chemicalsPresent
      })),
      stateSpecific: comprehensiveReport.regulatoryCompliance.stateRequirements.map(state => ({
        state: state.state,
        environmentalAgency: state.environmentalAgency,
        oshaProgram: state.oshaProgram
      }))
    },

    facilitySpecificRequirements: comprehensiveReport.facilityAnalysis.map((facility, idx) => ({
      facilityNumber: idx + 1,
      regulations: facility.regulatoryProfile.map(reg => ({
        regulation: reg.regulation,
        trigger: reg.trigger,
        requirement: reg.requirement,
        actionItems: reg.actionItems,
        costs: reg.costs,
        links: reg.links
      })),
      commonInspectionFindings: facility.commonInspectionFindings
    })),

    implementationRoadmap: {
      immediate: comprehensiveReport.implementationRoadmap.immediate,
      shortTerm: comprehensiveReport.implementationRoadmap.shortTerm,
      mediumTerm: comprehensiveReport.implementationRoadmap.mediumTerm,
      longTerm: comprehensiveReport.implementationRoadmap.longTerm
    },

    insuranceConsiderations: {
      currentPosture: formData.insuranceType,
      hasPollutionCoverage: formData.hasPollutionCoverage,
      // Note: Would include insurance considerations from report if needed
    },

    citations: comprehensiveReport.citations,

    suggestedQuestions: [
      "Help me develop a written Hazard Communication program that complies with 29 CFR 1910.1200",
      "What are the specific NPDES permit requirements for my facilities?",
      "Create a 90-day implementation plan for our highest priority compliance items",
      "Draft a contractor safety orientation checklist for our facilities",
      "What insurance policy endorsements should I specifically request from my broker?",
      "Help me prepare for an OSHA inspection - what will they look for?",
      "Create an employee training agenda for confined space entry",
      "What questions should I ask when interviewing EHS consultants?",
      "Draft a management of change procedure for our PSM program",
      "How do I calculate if we're a RCRA Large Quantity Generator?",
      "Create a monthly EHS compliance calendar for our team",
      "What should be in our emergency action plan?",
      "Help me write a letter requesting a pollution liability insurance quote",
      "Create a waste characterization form for our hazardous waste streams"
    ],

    usageInstructions: {
      step1: "Upload this JSON file to ChatGPT (use the paperclip icon), Claude (use the attachment button), or another AI assistant",
      step2: "The AI will automatically understand your company's EHS and insurance situation",
      step3: "Ask specific questions about compliance, implementation, or use the suggested questions above",
      step4: "Request the AI to draft policies, procedures, checklists, or training materials specific to your needs",
      tips: [
        "Be specific in your questions - reference particular facilities or regulations",
        "Ask for step-by-step implementation guides",
        "Request draft documents that you can customize",
        "Have the AI explain regulations in plain English",
        "Use it to prepare for audits or inspections",
        "Get help prioritizing multiple compliance requirements"
      ]
    }
  };

  return aiData;
};

export const downloadAIData = (comprehensiveReport, formData) => {
  const aiData = generateAIReadyData(comprehensiveReport, formData);

  // Create formatted JSON
  const jsonString = JSON.stringify(aiData, null, 2);

  // Create blob and download
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `EHS-Assessment-AI-Ready-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);

  // Show instructions
  alert(
    '✅ AI-Ready Data Downloaded!\n\n' +
    'HOW TO USE:\n' +
    '1. Upload this JSON file to ChatGPT, Claude, or any AI assistant\n' +
    '2. The AI will instantly understand your EHS situation\n' +
    '3. Ask questions or request help with compliance tasks\n\n' +
    'TRY ASKING:\n' +
    '• "Help me develop a Hazard Communication program"\n' +
    '• "Create a 90-day compliance implementation plan"\n' +
    '• "What will OSHA inspect at my facility?"\n' +
    '• "Draft employee safety training materials"'
  );
};
